import { createContext, useContext, useState } from "react";
import { useAuth } from "./AuthContext";
import { 
  listContacts, 
  getContactById, 
  createContact, 
  updateContact, 
  updateContactStatus, 
  deleteContact, 
  getContactStats,
  updateContactStage,
} from "../api/services";

const LOGO_COLORS = [
  { bg: "#ede9fe", color: "#7c3aed" },
  { bg: "#fef3c7", color: "#d97706" },
  { bg: "#fff7ed", color: "#ea580c" },
  { bg: "#f0fdf4", color: "#16a34a" },
  { bg: "#ecfdf5", color: "#059669" },
  { bg: "#fef9c3", color: "#ca8a04" },
  { bg: "#fef2f2", color: "#dc2626" },
  { bg: "#dbeafe", color: "#1d4ed8" },
];

const CompanyContext = createContext(null);

function toApiUpdatePayload(updates) {
  return {
    firstName: updates.firstName,
    lastName: updates.lastName,
    email: updates.email,
    phone: updates.phone,
    company: updates.company_name || updates.company,
    jobTitle: updates.jobTitle,
    address: updates.address?.street || updates.address,
    industry: updates.industry,
    website: updates.website,
    linkedIn: updates.linkedIn,
    notes: updates.notes,
    status: updates.status,
  };
}

export function CompanyProvider({ children }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  const loadCompanies = async (params = {}) => {
    if (!user) {
      console.log("No user, skipping loadCompanies");
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const data = await listContacts(params);
      const mappedCompanies = (data.content || []).map(contact => ({
        id: contact.id,
        company_name: contact.company,
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        phone: contact.phone,
        jobTitle: contact.jobTitle,
        industry: contact.industry,
        address: contact.address ? { street: contact.address, region: "", country: "" } : { street: "", region: "", country: "" },
        website: contact.website,
        linkedIn: contact.linkedIn,
        notes: contact.notes,
        status: contact.status,
        pipelineStage: contact.pipelineStage || "PROSPECTING",
        logo: contact.company?.substring(0, 2).toUpperCase() || "NA",
        logoColor: LOGO_COLORS[Math.floor(Math.random() * LOGO_COLORS.length)],
        contactPersons: contact.contactPersons || [],
      }));
      setCompanies(mappedCompanies);
      return data;
    } catch (err) {
      console.error("Failed to load companies:", err);
      setError(err.message || "Failed to load companies");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!user) {
      console.log("No user, skipping loadStats");
      return;
    }
    
    try {
      const data = await getContactStats();
      setStats(data);
      return data;
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  };

  const addCompany = async (company) => {
    if (!user) throw new Error("Not authenticated");
    
    try {
      const initials = company.company.substring(0, 2).toUpperCase();
      const logoColor = LOGO_COLORS[companies.length % LOGO_COLORS.length];
      
      const response = await createContact(company);
      
      const createdCompany = {
        id: response.id,
        company_name: response.company,
        firstName: response.firstName,
        lastName: response.lastName,
        email: response.email,
        phone: response.phone,
        jobTitle: response.jobTitle,
        industry: response.industry,
        address: response.address ? { street: response.address, region: "", country: "" } : { street: "", region: "", country: "" },
        website: response.website,
        linkedIn: response.linkedIn,
        notes: response.notes,
        status: response.status,
        pipelineStage: response.pipelineStage || "PROSPECTING",
        logo: initials,
        logoColor: logoColor,
        contactPersons: [],
      };
      
      setCompanies((prev) => [createdCompany, ...prev]);
      await loadStats();
      return createdCompany;
    } catch (err) {
      console.error("Failed to add company:", err);
      throw err;
    }
  };

  const updateCompany = async (id, updates) => {
    if (!user) throw new Error("Not authenticated");
    
    try {
      const updateData = toApiUpdatePayload(updates);
      const response = await updateContact(id, updateData);
      
      const updatedCompany = {
        ...response,
        company_name: response.company,
        address: response.address ? { street: response.address, region: "", country: "" } : { street: "", region: "", country: "" },
      };
      
      setCompanies((prev) => prev.map((c) => c.id === id ? { ...c, ...updatedCompany } : c));
      await loadStats();
      return response;
    } catch (err) {
      console.error("Failed to update company:", err);
      throw err;
    }
  };

  const updateCompanyStatus = async (id, status) => {
    if (!user) throw new Error("Not authenticated");
    
    try {
      const response = await updateContactStatus(id, status);
      setCompanies((prev) => prev.map((c) => c.id === id ? { ...c, status: response.status } : c));
      await loadStats();
      return response;
    } catch (err) {
      console.error("Failed to update status:", err);
      throw err;
    }
  };

  const deleteCompany = async (id) => {
    if (!user) throw new Error("Not authenticated");
    
    try {
      await deleteContact(id);
      setCompanies((prev) => prev.filter((c) => c.id !== id));
      await loadStats();
    } catch (err) {
      console.error("Failed to delete company:", err);
      throw err;
    }
  };

  const moveCompanyStage = async (id, newStage) => {
    if (!user) throw new Error("Not authenticated");
    
    try {
      const response = await updateContactStage(id, newStage);
      setCompanies((prev) => prev.map((c) => {
        if (c.id !== id) return c;
        return { ...c, pipelineStage: response.pipelineStage || newStage };
      }));
      await loadStats();
    } catch (err) {
      console.error("Failed to move stage:", err);
      throw err;
    }
  };

  // FIXED: was sending the raw frontend-shaped `currentCompany` object
  // (company_name, nested address object, logo/logoColor, fake Date.now() id)
  // straight to the backend, which caused the 500. Now builds the same
  // clean DTO shape that updateCompany uses, and lets the backend assign
  // the new person's real id instead of a client-side timestamp.
  const addContactPerson = async (companyId, person) => {
    if (!user) throw new Error("Not authenticated");
    
    try {
      const currentCompany = companies.find(c => c.id === companyId);
      if (!currentCompany) throw new Error("Company not found");
      
      const updatedContactPersons = [...(currentCompany.contactPersons || []), person];
      const updateData = {
        ...toApiUpdatePayload(currentCompany),
        contactPersons: updatedContactPersons,
      };
      
      const response = await updateContact(companyId, updateData);
      setCompanies((prev) => prev.map((c) => c.id === companyId ? { ...c, contactPersons: response.contactPersons } : c));
      return response;
    } catch (err) {
      console.error("Failed to add contact person:", err);
      throw err;
    }
  };

  const getCompanyById = async (id) => {
    if (!user) throw new Error("Not authenticated");
    
    try {
      const company = await getContactById(id);
      return {
        ...company,
        company_name: company.company,
        address: company.address ? { street: company.address, region: "", country: "" } : { street: "", region: "", country: "" },
      };
    } catch (err) {
      console.error("Failed to get company:", err);
      throw err;
    }
  };

  return (
    <CompanyContext.Provider value={{ 
      companies, 
      loading,
      error,
      stats,
      addCompany, 
      updateCompany, 
      updateCompanyStatus,
      deleteCompany, 
      moveCompanyStage, 
      addContactPerson,
      loadCompanies,
      loadStats,
      getCompanyById
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompanies() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompanies must be used inside CompanyProvider");
  return ctx;
}